import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Search, Users, Filter } from 'lucide-react';
import LoadingSpinner from '../../common/LoadingSpinner';
import { memberMatchesNameOrEmailSearch } from '../../../utils/memberSearchMatch';

export default function MemberCheckboxList({ selectedMembers, onSelectionChange }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    useEffect(() => {
        fetchMembers();
    }, []);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const api = getAuthenticatedApi();
            
            const gymResponse = await api.get('/gyms.php');
            if (gymResponse.data.length > 0) {
                const gymId = gymResponse.data[0].id;
                const membersResponse = await api.get(`/gyms.php?action=members&gym_id=${gymId}`);
                // Filtra solo membri (non manager)
                const membersList = membersResponse.data.filter(m => m.role_in_gym !== 'manager');
                setMembers(membersList);
            }
        } catch (err) {
            console.error('Errore caricamento membri:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredMembers = members.filter(member => {
        const matchesSearch = memberMatchesNameOrEmailSearch(member, searchTerm);
        const matchesRole = roleFilter === 'all' || member.role_in_gym === roleFilter;
        const matchesStatus = statusFilter === 'all' || member.membership_status === statusFilter;
        
        return matchesSearch && matchesRole && matchesStatus;
    });

    const handleToggle = (memberId) => {
        const newSelection = selectedMembers.includes(memberId)
            ? selectedMembers.filter(id => id !== memberId)
            : [...selectedMembers, memberId];
        onSelectionChange(newSelection);
    };

    const handleSelectAll = () => {
        const allIds = filteredMembers.map(m => m.id);
        onSelectionChange(allIds);
    };

    const handleDeselectAll = () => {
        onSelectionChange([]);
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-4 h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
                <Users className="text-blue-600" size={24} />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    1️⃣ {t('gym.bulkEnrollment.members.title')}
                </h3>
            </div>

            {/* Filtri */}
            <div className="space-y-3 mb-4">
                {/* Ricerca */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder={t('gym.bulkEnrollment.members.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>

                {/* Filtri Ruolo e Stato */}
                <div className="grid grid-cols-2 gap-2">
                    <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">{t('gym.bulkEnrollment.members.allRoles')}</option>
                        <option value="member">{t('gym.bulkEnrollment.members.members')}</option>
                        <option value="trainer">{t('gym.bulkEnrollment.members.trainer')}</option>
                    </select>

                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">{t('gym.bulkEnrollment.members.allStatus')}</option>
                        <option value="active">{t('gym.bulkEnrollment.members.active')}</option>
                        <option value="inactive">{t('gym.bulkEnrollment.members.inactive')}</option>
                    </select>
                </div>

                {/* Azioni Selezione */}
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-white/65">
                        {selectedMembers.length}/{filteredMembers.length} {t('gym.bulkEnrollment.members.selected')}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={handleSelectAll}
                            className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                            {t('gym.bulkEnrollment.members.selectAll')}
                        </button>
                        <span className="text-gray-400">|</span>
                        <button
                            onClick={handleDeselectAll}
                            className="text-gray-600 hover:text-gray-700 dark:text-white/65 dark:hover:text-gray-300 font-medium"
                        >
                            {t('gym.bulkEnrollment.members.deselectAll')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Lista Membri */}
            <div className="flex-1 overflow-y-auto space-y-2">
                {filteredMembers.length > 0 ? (
                    filteredMembers.map(member => (
                        <label
                            key={member.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                selectedMembers.includes(member.id)
                                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
                                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:brightness-110'
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={selectedMembers.includes(member.id)}
                                onChange={() => handleToggle(member.id)}
                                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-white font-semibold text-sm">
                                    {member.name.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                    {member.name}
                                </p>
                                <p className="text-gray-500 dark:text-white/65 text-xs truncate">
                                    {member.email}
                                </p>
                                <div className="flex gap-1 mt-1">
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                                        member.role_in_gym === 'trainer'
                                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                                    }`}>
                                        {member.role_in_gym === 'trainer' ? t('gym.bulkEnrollment.members.trainer') : t('gym.bulkEnrollment.members.member')}
                                    </span>
                                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
                                        member.membership_status === 'active'
                                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    }`}>
                                        {member.membership_status === 'active' ? t('gym.bulkEnrollment.members.activeStatus') : t('gym.bulkEnrollment.members.inactiveStatus')}
                                    </span>
                                </div>
                            </div>
                        </label>
                    ))
                ) : (
                    <div className="text-center py-8">
                        <Users size={48} className="mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500 dark:text-white/65 text-sm">
                            {t('gym.bulkEnrollment.members.noMembersFound')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

